import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Button,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore, Edit } from "@mui/icons-material";
import PlecaStyleForm from "./globalStylesSection/components/PlecaStyleForm";
import StyleAttributeForm from "./globalStylesSection/components/StyleAttributeForm";
import {
  AVAILABLE_FONTS,
  AVAILABLE_POSITIONS,
  STYLE_ATTRIBUTES_SPANISH,
} from "./globalStylesSection/constants/styleDefaults.js";

const SPANISH_LABEL_MAP = Object.freeze(
  Object.fromEntries(STYLE_ATTRIBUTES_SPANISH.map((a) => [a.id, a.label]))
);

const SectionStyleCard = React.memo(function SectionStyleCard({
  section,
  sectionStyles = {},
  pending,
  expanded,
  onToggle,
  onEdit,
  onLocalChange,
  onOpenColorPicker,
}) {
  const attributeNames = Object.keys(sectionStyles);

  return (
    <Card sx={{ borderRadius: "16px" }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {section.name}
            {section.styleTypeName ? ` - ${section.styleTypeName}` : ""}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => onEdit(section)}
              sx={{
                borderRadius: "8px",
                textTransform: "capitalize",
                minWidth: "100px",
                borderColor: "#1976d2",
                color: "#1976d2",
                "&:hover": {
                  borderColor: "#1565c0",
                  color: "#1565c0",
                  bgcolor: "#e3f2fd",
                },
              }}
            >
              Editar
            </Button>
            <Button
              variant="outlined"
              startIcon={
                <ExpandMore
                  sx={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              }
              onClick={() => onToggle(section.id)}
              sx={{
                borderRadius: "8px",
                textTransform: "capitalize",
                minWidth: "120px",
              }}
            >
              {expanded ? "Contraer" : "Expandir"}
            </Button>
          </Stack>
        </Box>

        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={1}>
            {attributeNames.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Editar el tipo de sección para habilitar los atributos y poder editar los estilos
              </Typography>
            )}

            {attributeNames.map((attr) => {
              const styles = sectionStyles[attr] || {};
              const hasChanges = Boolean(pending?.[attr]);

              return (
                <Accordion
                  key={attr}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    "&.Mui-expanded": { margin: 0 },
                    "&::before": { display: "none" },
                  }}
                  TransitionProps={{ unmountOnExit: true }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      backgroundColor: hasChanges ? "#e3f2fd" : "transparent",
                      borderRadius: "8px 8px 0 0",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {attr}
                      </Typography>
                      {hasChanges && (
                        <Chip
                          label="Cambios pendientes"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0 }}>
                    {attr === "Color de Pleca" || attr === "pleca" ? (
                      <PlecaStyleForm
                        attributeId={attr}
                        label={SPANISH_LABEL_MAP[attr] || attr}
                        values={styles}
                        onChange={(id, vals) => onLocalChange(section.id, id, vals)}
                        onOpenColorPicker={(id, color) =>
                          onOpenColorPicker(
                            color,
                            (newColor) =>
                              onLocalChange(section.id, id, { color: newColor }),
                            "Seleccionar color de fondo"
                          )
                        }
                      />
                    ) : (
                      <StyleAttributeForm
                        attributeId={attr}
                        label={SPANISH_LABEL_MAP[attr] || attr}
                        values={styles}
                        onChange={(id, vals) => onLocalChange(section.id, id, vals)}
                        onOpenColorPicker={(id, color) =>
                          onOpenColorPicker(
                            color,
                            (newColor) =>
                              onLocalChange(section.id, id, { color: newColor }),
                            "Seleccionar color de texto"
                          )
                        }
                        fonts={AVAILABLE_FONTS}
                        positions={AVAILABLE_POSITIONS}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
});

export default SectionStyleCard;
